// Smart persona switch — per operator instruction (2026-05-22):
// "if person have one brand switch to SMB on second brand switch to Agency mode"
//
// The home page composition + sidebar emphasis re-arranges based on:
//   - workspace.persona (explicit operator preference; wins if set)
//   - brand count (derived; 1 → smb, 2+ → agency)
//
// Freelancer + creator personas are explicit-only — never derived.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Brand, Persona, Workspace } from "@/lib/types";

const SMB_THRESHOLD = 1;

interface MeResponse {
  user: { id: string; email: string; display_name: string };
  workspace: Workspace;
  brand_count: number;
}

export function usePersona(): { persona: Persona; isLoading: boolean } {
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeResponse>("/auth/me"),
    staleTime: 60_000,
  });

  const persona = useMemo<Persona>(() => {
    if (!me.data) return "smb"; // optimistic default while loading
    const explicit = me.data.workspace.persona;
    // freelancer + creator are explicit only — never derived
    if (explicit === "freelancer" || explicit === "creator") return explicit;
    // smb vs agency derived from brand count
    return me.data.brand_count <= SMB_THRESHOLD ? "smb" : "agency";
  }, [me.data]);

  return { persona, isLoading: me.isLoading };
}

export function useBrands() {
  return useQuery({
    queryKey: ["brands"],
    // brand-service currently returns { brands: [...] } (legacy shape).
    // API.html standardises on { data: [...] }. Normalise here so
    // every downstream caller can read r.data regardless of which
    // envelope the backend ships. (Caught 2026-05-22 — the new shell
    // saw r.data and quietly rendered "0 marka" while two brands
    // sat in the DB.)
    queryFn: async () => {
      const r = await api<{ data?: Brand[]; brands?: Brand[]; next_cursor?: string | null }>("/brands");
      return {
        data: r.data ?? r.brands ?? [],
        next_cursor: r.next_cursor ?? null,
      };
    },
    staleTime: 60_000,
  });
}
